// Ask a question (ENG-3223; ENG-3222 decisions 18-21): a member asks in plain
// words, the model picks one governed shape (a measure, a breakdown, filters
// and a window) and names the card, and our code runs the query and writes
// every number (Mason, 6 October). The model never writes SQL. Most measures
// read Swivl's own tables; milestone 7's read Swivl's analytics through
// Omni's query API, with the same scoping and permissions.
import { ReviewPlatform } from "../Reviews/Review.Interfaces";
import { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import { PulseChart, PulseHeadline } from "./Pulse.Candidates";
import { PulseChannel } from "./Pulse.Facts";
import { PulseLens, PulsePersona } from "./Pulse.Personas";

/** A member may ask this many questions in any 24 hours (decision 21); asks that fail on Pulse's side don't count. */
export const PULSE_QUESTIONS_PER_DAY = 10;

/** An org may have this many questions pinned, across its members (decision 21). */
export const PULSE_PINNED_QUESTIONS_PER_ORG = 20;

/** The longest question a member can ask, in characters. */
export const PULSE_QUESTION_MAX_LENGTH = 300;

/** What a question can measure: each one's scope, unit and definition are in PULSE_QUESTION_MEASURES, its SQL or Omni query on the server. */
export enum PulseQuestionMeasure {
  CONVERSATIONS = "conversations",
  AVERAGE_WAIT = "average-wait",
  HEALTH = "health",
  ABANDONMENT = "abandonment",
  ESCALATION_SHARE = "escalation-share",
  OPEN_TICKETS = "open-tickets",
  OVERDUE_TICKETS = "overdue-tickets",
  COMPLETED_TICKETS = "completed-tickets",
  REVIEWS = "reviews",
  AVERAGE_RATING = "average-rating",
  UNREPLIED_REVIEWS = "unreplied-reviews",
  /** Swivl staff only. */
  PULSE_ADOPTION = "pulse-adoption",
  // From Swivl's analytics, through Omni (ENG-3222 milestone 7).
  MOVE_INS = "move-ins",
  LEADS_SENT = "leads-sent",
  MOVE_IN_MESSAGES = "move-in-messages",
  MOVE_OUT_MESSAGES = "move-out-messages",
  PAST_DUE_REMINDERS = "past-due-reminders",
  ABANDONED_CART_MESSAGES = "abandoned-cart-messages",
  SELF_SERVICE_ANSWERS = "self-service-answers",
  FEEDBACK_RESPONSES = "feedback-responses",
  REVIEW_PROMPTS_ACCEPTED = "review-prompts-accepted",
  AI_HANDLED_CALLS = "ai-handled-calls"
}

/** How the answer is split. "none" is one total. */
export type PulseQuestionBreakdown = "none" | "location" | "group" | "channel" | "platform" | "day" | "week";

/** The window, in the org's zone. "last-7-days" and "last-30-days" include today. */
export type PulseQuestionWindow = "today" | "yesterday" | "last-7-days" | "last-30-days" | "this-month" | "last-month";

/** One governed question: what the model picks, and all the server will run. */
export interface PulseQuestionShape {
  measure: PulseQuestionMeasure;
  breakdown: PulseQuestionBreakdown;
  window: PulseQuestionWindow;
  /** Only that channel's conversations. */
  channel: PulseChannel | null;
  /** Only this location, which must be in the member's reach. */
  locationId: number | null;
  /** Only this group's locations in the member's reach. */
  groupId: number | null;
  /** Only this platform's reviews. */
  platform: ReviewPlatform | null;
}

/** "Of N conversations, M had a location": how much of the answer could be placed. */
export interface PulseQuestionCoverage {
  total: number;
  located: number;
}

/** A question's answer, written by code from the query. */
export interface PulseQuestionAnswer {
  headline: PulseHeadline;
  chart: PulseChart;
  /** What was computed, in plain English, from the documented columns. */
  definition: string;
  /** "Last 7 days", "Now". */
  windowLabel: string;
  /** Null for a count of what's open now. */
  window: { from: string; to: string } | null;
  coverage: PulseQuestionCoverage | null;
  /** When the query ran. */
  asOf: string;
}

/** asking: the AI or the query is at work; cannot: the AI found no governed question; denied: no permission; failed: our side. */
export type PulseQuestionStatus = "asking" | "answered" | "cannot" | "denied" | "failed";

export interface PulseQuestionInterface extends OrgBaseInterface {
  memberId: number;
  /** As the member typed it. */
  question: string;
  status: PulseQuestionStatus;
  shape: PulseQuestionShape | null;
  answer: PulseQuestionAnswer | null;
  /** The AI's card title: a few words, never a number. */
  title: string | null;
  /** Why it wasn't answered, for the member. */
  reason: string | null;
  /** Pinned questions take grid positions like pins, and re-run each time the member's page is written. */
  pinnedAt: Date | null;
  position: number | null;
}

/** The member's question, as the open slot sends it. */
export interface PulseAskRequest {
  question: string;
}

/** A location or group the member can ask about, by name, for the model to match the question's words to. */
export interface PulseAskPlace {
  id: number;
  name: string;
}

/** What the server sends the bot: the question and the member's places, never their data. */
export interface PulseAskRouteRequest {
  orgId: number;
  memberId: number;
  /** The question's row, for the receipt. */
  questionId: number;
  persona: PulsePersona;
  question: string;
  /** The org's time zone, so the model reads "today" and "September" as the member does. */
  timezone: string;
  locations: PulseAskPlace[];
  groups: PulseAskPlace[];
}

/**
 * The model's choice: one governed question and its card's title, or why
 * none fits. The server checks the shape against the registry and the
 * member's reach before anything runs.
 */
export type PulseAskRoute = { kind: "question"; shape: unknown; title: string } | { kind: "cannot"; reason: string };

// The governed questions (ENG-3223; ENG-3222 decisions 18-21, Mason 5-6
// October): every measure a member can ask about, whose reach it reads, and
// what it means in plain English. The model picks one of these and fills in
// the rest of the shape; nothing it says reaches the SQL except values these
// lists allow.

/** Whose reach a measure reads, which is also the permission a member needs to ask it. */
export type PulseQuestionScope = "conversations" | "tickets" | "reviews" | "leads" | "staff";

/** Where a measure is counted: Swivl's own tables, by Pulse's SQL, or Swivl's analytics, through Omni. */
export type PulseQuestionSource = "database" | "omni";

export interface PulseMeasureDefinition {
  measure: PulseQuestionMeasure;
  /** The headline's label. */
  label: string;
  scope: PulseQuestionScope;
  source: PulseQuestionSource;
  kind: PulseHeadline["kind"];
  unit: PulseHeadline["unit"];
  lens: PulseLens;
  /** False for what's open now, which ignores the window. */
  windowed: boolean;
  /** The windows it can answer for. */
  windows: PulseQuestionWindow[];
  breakdowns: PulseQuestionBreakdown[];
  /** What was computed, from the documented columns: the card's detail, so 25 words or fewer. */
  definition: string;
}

export const PULSE_QUESTION_WINDOWS: PulseQuestionWindow[] = ["today", "yesterday", "last-7-days", "last-30-days", "this-month", "last-month"];

const CONVERSATION_BREAKDOWNS: PulseQuestionBreakdown[] = ["none", "location", "group", "channel", "day", "week"];
const TICKET_BREAKDOWNS: PulseQuestionBreakdown[] = ["none", "location", "group"];
const REVIEW_BREAKDOWNS: PulseQuestionBreakdown[] = ["none", "location", "group", "platform", "day", "week"];

type OwnMeasure = Omit<PulseMeasureDefinition, "scope" | "source" | "lens" | "windowed" | "windows" | "breakdowns">;

const conversations = (definition: OwnMeasure): PulseMeasureDefinition => ({
  ...definition,
  scope: "conversations",
  source: "database",
  lens: PulseLens.SERVICE,
  windowed: true,
  windows: PULSE_QUESTION_WINDOWS,
  breakdowns: CONVERSATION_BREAKDOWNS
});

const reviews = (definition: OwnMeasure): PulseMeasureDefinition => ({
  ...definition,
  scope: "reviews",
  source: "database",
  lens: PulseLens.DEMAND_REPUTATION,
  windowed: true,
  windows: PULSE_QUESTION_WINDOWS,
  breakdowns: REVIEW_BREAKDOWNS
});

// Swivl's analytics (ENG-3222 milestone 7): counts rebuilt by the analytics
// team's dbt jobs, nightly or every four hours, and read through Omni.
// Omni dates them in its connection's zone, Eastern, so a day is an Eastern
// day whatever the org's zone. Today is never asked: it isn't in yet.
const ANALYTICS_BREAKDOWNS: PulseQuestionBreakdown[] = ["none", "location", "group", "day", "week"];
const ANALYTICS_WINDOWS = PULSE_QUESTION_WINDOWS.filter((window) => window !== "today");
const NIGHTLY = "From Swivl's analytics, updated nightly; days are Eastern time.";
const EVERY_FOUR_HOURS = "From Swivl's analytics, updated every four hours; days are Eastern time.";

const analytics = (
  definition: Pick<PulseMeasureDefinition, "measure" | "label" | "scope" | "lens">,
  counted: string,
  refreshed: string
): PulseMeasureDefinition => ({
  ...definition,
  source: "omni",
  kind: "count",
  unit: "count",
  windowed: true,
  windows: ANALYTICS_WINDOWS,
  breakdowns: ANALYTICS_BREAKDOWNS,
  definition: `${counted} ${refreshed}`
});

// LINK apps/server/src/API/Pulse/PulseQuestion.Queries.ts
export const PULSE_QUESTION_MEASURES: Record<PulseQuestionMeasure, PulseMeasureDefinition> = {
  [PulseQuestionMeasure.CONVERSATIONS]: conversations({
    measure: PulseQuestionMeasure.CONVERSATIONS,
    label: "Conversations",
    kind: "count",
    unit: "count",
    definition: "Conversations customers started, on every channel, live ones included. Agent, campaign and system conversations aren't counted."
  }),
  [PulseQuestionMeasure.AVERAGE_WAIT]: conversations({
    measure: PulseQuestionMeasure.AVERAGE_WAIT,
    label: "Average wait",
    kind: "average",
    unit: "seconds",
    definition:
      "From a conversation's first request for a person to the first agent accepting it. Live waits count up to now; unanswered ended ones don't."
  }),
  [PulseQuestionMeasure.HEALTH]: conversations({
    measure: PulseQuestionMeasure.HEALTH,
    label: "Health",
    kind: "average",
    unit: "score",
    definition: "The average health score, 0 to 100, of conversations that have ended."
  }),
  [PulseQuestionMeasure.ABANDONMENT]: conversations({
    measure: PulseQuestionMeasure.ABANDONMENT,
    label: "Abandoned or unresolved",
    kind: "rate",
    unit: "percent",
    definition: "Of conversations judged when they closed, the share that ended unresolved or abandoned."
  }),
  [PulseQuestionMeasure.ESCALATION_SHARE]: conversations({
    measure: PulseQuestionMeasure.ESCALATION_SHARE,
    label: "Asked for a person",
    kind: "rate",
    unit: "percent",
    definition: "Of conversations customers started, the share offered to an agent or routed to find one."
  }),
  [PulseQuestionMeasure.OPEN_TICKETS]: {
    measure: PulseQuestionMeasure.OPEN_TICKETS,
    label: "Open tickets",
    scope: "tickets",
    source: "database",
    kind: "count",
    unit: "count",
    lens: PulseLens.OPERATIONS,
    windowed: false,
    windows: PULSE_QUESTION_WINDOWS,
    breakdowns: TICKET_BREAKDOWNS,
    definition: "Tickets to do, in progress or on hold right now, that you can open in Ticketing."
  },
  [PulseQuestionMeasure.OVERDUE_TICKETS]: {
    measure: PulseQuestionMeasure.OVERDUE_TICKETS,
    label: "Overdue tickets",
    scope: "tickets",
    source: "database",
    kind: "count",
    unit: "count",
    lens: PulseLens.OPERATIONS,
    windowed: false,
    windows: PULSE_QUESTION_WINDOWS,
    breakdowns: TICKET_BREAKDOWNS,
    definition: "Open tickets whose due date has passed, that you can open in Ticketing."
  },
  [PulseQuestionMeasure.COMPLETED_TICKETS]: {
    measure: PulseQuestionMeasure.COMPLETED_TICKETS,
    label: "Completed tickets",
    scope: "tickets",
    source: "database",
    kind: "count",
    unit: "count",
    lens: PulseLens.OPERATIONS,
    windowed: true,
    windows: PULSE_QUESTION_WINDOWS,
    breakdowns: TICKET_BREAKDOWNS,
    definition: "Tickets marked completed in the window, each ticket once, that you can open in Ticketing. Cancelled tickets aren't counted."
  },
  [PulseQuestionMeasure.REVIEWS]: reviews({
    measure: PulseQuestionMeasure.REVIEWS,
    label: "Reviews",
    kind: "count",
    unit: "count",
    definition: "Reviews posted in the window, on every platform. Archived reviews aren't counted."
  }),
  [PulseQuestionMeasure.AVERAGE_RATING]: reviews({
    measure: PulseQuestionMeasure.AVERAGE_RATING,
    label: "Average rating",
    kind: "average",
    unit: "rating",
    definition: "The average star rating of reviews posted in the window. Archived reviews aren't counted."
  }),
  [PulseQuestionMeasure.UNREPLIED_REVIEWS]: reviews({
    measure: PulseQuestionMeasure.UNREPLIED_REVIEWS,
    label: "Unreplied reviews",
    kind: "count",
    unit: "count",
    definition: "Reviews posted in the window that have no reply yet. Archived reviews aren't counted."
  }),
  [PulseQuestionMeasure.PULSE_ADOPTION]: {
    measure: PulseQuestionMeasure.PULSE_ADOPTION,
    label: "Opened Pulse",
    scope: "staff",
    source: "database",
    kind: "rate",
    unit: "percent",
    lens: PulseLens.CUSTOMER_HEALTH,
    windowed: false,
    windows: PULSE_QUESTION_WINDOWS,
    breakdowns: ["none"],
    definition: "Of the org's own members with a Pulse persona, the share who opened Pulse in the last 7 days. Swivl's logins aren't counted."
  },
  // Sales follow the CRM view permission, as lead counts do on the page (ENG-3225); the rest follow conversations (Mason, 6 October).
  [PulseQuestionMeasure.MOVE_INS]: analytics(
    { measure: PulseQuestionMeasure.MOVE_INS, label: "Move-ins", scope: "leads", lens: PulseLens.REVENUE },
    "Move-ins credited to Swivl, each counted once.",
    NIGHTLY
  ),
  [PulseQuestionMeasure.LEADS_SENT]: analytics(
    { measure: PulseQuestionMeasure.LEADS_SENT, label: "Leads sent", scope: "leads", lens: PulseLens.DEMAND_REPUTATION },
    "Leads Swivl sent to your management software, one per person.",
    NIGHTLY
  ),
  [PulseQuestionMeasure.MOVE_IN_MESSAGES]: analytics(
    { measure: PulseQuestionMeasure.MOVE_IN_MESSAGES, label: "Move-in messages", scope: "conversations", lens: PulseLens.OPERATIONS },
    "Texts and emails from Swivl's move-in sequence.",
    EVERY_FOUR_HOURS
  ),
  [PulseQuestionMeasure.MOVE_OUT_MESSAGES]: analytics(
    { measure: PulseQuestionMeasure.MOVE_OUT_MESSAGES, label: "Move-out messages", scope: "conversations", lens: PulseLens.OPERATIONS },
    "Texts and emails from Swivl's move-out sequence.",
    NIGHTLY
  ),
  [PulseQuestionMeasure.PAST_DUE_REMINDERS]: analytics(
    { measure: PulseQuestionMeasure.PAST_DUE_REMINDERS, label: "Past-due reminders", scope: "conversations", lens: PulseLens.FINANCE },
    "Texts, emails and calls from Swivl's past-due sequence.",
    EVERY_FOUR_HOURS
  ),
  [PulseQuestionMeasure.ABANDONED_CART_MESSAGES]: analytics(
    { measure: PulseQuestionMeasure.ABANDONED_CART_MESSAGES, label: "Abandoned-cart messages", scope: "conversations", lens: PulseLens.REVENUE },
    "Texts, emails and calls from Swivl's abandoned-cart sequence.",
    NIGHTLY
  ),
  [PulseQuestionMeasure.SELF_SERVICE_ANSWERS]: analytics(
    { measure: PulseQuestionMeasure.SELF_SERVICE_ANSWERS, label: "Self-service answers", scope: "conversations", lens: PulseLens.SERVICE },
    "Gate code, unit number and balance requests Swivl answered.",
    NIGHTLY
  ),
  [PulseQuestionMeasure.FEEDBACK_RESPONSES]: analytics(
    { measure: PulseQuestionMeasure.FEEDBACK_RESPONSES, label: "Feedback responses", scope: "conversations", lens: PulseLens.DEMAND_REPUTATION },
    "Feedback customers gave Swivl, after review requests or in web chat.",
    NIGHTLY
  ),
  [PulseQuestionMeasure.REVIEW_PROMPTS_ACCEPTED]: analytics(
    {
      measure: PulseQuestionMeasure.REVIEW_PROMPTS_ACCEPTED,
      label: "Review prompts accepted",
      scope: "conversations",
      lens: PulseLens.DEMAND_REPUTATION
    },
    "Customers who agreed to leave a Google review when Swivl asked.",
    NIGHTLY
  ),
  [PulseQuestionMeasure.AI_HANDLED_CALLS]: analytics(
    { measure: PulseQuestionMeasure.AI_HANDLED_CALLS, label: "Calls the AI handled", scope: "conversations", lens: PulseLens.SERVICE },
    "Calls the AI handled without a transfer; silent callers aren't counted.",
    NIGHTLY
  )
};

export const PULSE_QUESTION_BREAKDOWNS: PulseQuestionBreakdown[] = ["none", "location", "group", "channel", "platform", "day", "week"];

/** Day and week buckets need a window long enough to have a few. */
const BUCKETED_WINDOWS: PulseQuestionWindow[] = ["last-7-days", "last-30-days", "this-month", "last-month"];

/** The channels a conversation question can be narrowed to. */
export const PULSE_QUESTION_CHANNELS: PulseChannel[] = ["web", "sms", "voice"];
const PLATFORMS: ReviewPlatform[] = Object.values(ReviewPlatform);

/** A shape the model chose, checked against the lists; the reason is for the member when it doesn't fit. */
export type PulseShapeCheck = { shape: PulseQuestionShape } | { reason: string };

function pick<T extends string>(values: readonly T[], value: unknown): T | undefined {
  return values.find((candidate) => candidate === value);
}

function optionalId(value: unknown): number | null | undefined {
  if (value === null || value === undefined) return null;
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : undefined;
}

/**
 * Checks a shape against the registry, filling absent filters with null.
 * Nothing about the member is checked here: permission and reach are the
 * caller's, against the member's own login.
 */
export function checkQuestionShape(raw: unknown): PulseShapeCheck {
  const input = (raw ?? {}) as Record<string, unknown>;
  const measure = pick(Object.values(PulseQuestionMeasure), input.measure);
  if (!measure) return { reason: "Pulse can't measure that yet." };
  const definition = PULSE_QUESTION_MEASURES[measure];
  const breakdown = pick(definition.breakdowns, input.breakdown ?? "none");
  if (!breakdown) return { reason: `${definition.label} can't be split that way.` };
  const window = pick(PULSE_QUESTION_WINDOWS, input.window ?? "last-7-days");
  if (!window) return { reason: "Pulse can't answer for that time." };
  if (!definition.windows.includes(window)) {
    if (definition.source === "omni" && window === "today") {
      return { reason: "Swivl's analytics don't have today's numbers yet. Try yesterday or a longer window." };
    }
    return { reason: "Pulse can't answer for that time." };
  }
  if ((breakdown === "day" || breakdown === "week") && !BUCKETED_WINDOWS.includes(window)) {
    return { reason: "A day-by-day answer needs at least the last 7 days." };
  }

  // A measure narrows to what it can be split by: Omni's conversation measures have no channel.
  const channel = input.channel === null || input.channel === undefined ? null : pick(PULSE_QUESTION_CHANNELS, input.channel);
  if (channel === undefined || (channel !== null && !definition.breakdowns.includes("channel"))) {
    return { reason: `${definition.label} can't be narrowed to a channel.` };
  }
  const platform = input.platform === null || input.platform === undefined ? null : pick(PLATFORMS, input.platform);
  if (platform === undefined || (platform !== null && !definition.breakdowns.includes("platform"))) {
    return { reason: `${definition.label} can't be narrowed to a review platform.` };
  }
  const locationId = optionalId(input.locationId);
  const groupId = optionalId(input.groupId);
  if (locationId === undefined || groupId === undefined) return { reason: "Pulse couldn't tell which location you meant." };
  if (locationId !== null && groupId !== null) return { reason: "Ask about one location or one group, not both." };
  if ((locationId !== null || groupId !== null) && definition.scope === "staff") {
    return { reason: `${definition.label} can't be narrowed to a location.` };
  }

  return { shape: { measure, breakdown, window, channel, locationId, groupId, platform } };
}
